import { Form, Head, router } from '@inertiajs/react'
import { Trash2 } from 'lucide-react'
import Heading from '@/components/heading'
import InputError from '@/components/input-error'
import MultiplePathsInput from '@/components/multiple-paths-input'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import AppLayout from '@/layouts/app-layout'
import { serversPath, appPath, backupConfigPath, editBackupConfigPath } from '@/routes'
import type { BreadcrumbItem } from '@/types'
import type { App, BackupConfig } from '@/types'

type BackupConfigEditProps = {
  backup_config: BackupConfig & { app: Pick<App, 'id' | 'name'> }
}

export default function BackupConfigEdit({ backup_config }: BackupConfigEditProps) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: 'Servers', href: serversPath() },
    { title: backup_config.app.name, href: appPath(backup_config.app.id) },
    { title: 'Edit Backup Config', href: editBackupConfigPath(backup_config.id) },
  ]

  function handleDelete() {
    if (confirm('Are you sure you want to delete this backup configuration? This will also delete all associated backups.')) {
      router.delete(backupConfigPath(backup_config.id))
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Edit Backup Configuration" />

      <div className="p-6">
        <Heading
        title="Edit Backup Configuration"
        description={`Update backup settings for ${backup_config.app.name}`}
      />

      <Card className="mt-6 max-w-2xl">
        <CardHeader>
          <CardTitle>Backup Settings</CardTitle>
          <CardDescription>
            Modify the backup configuration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form
            method="put"
            action={backupConfigPath(backup_config.id)}
            className="space-y-6"
          >
            {({ processing, errors }) => (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="backup_type">Backup Type *</Label>
                    <select
                      id="backup_type"
                      name="backup_config[backup_type]"
                      required
                      defaultValue={backup_config.backup_type}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="directory">Directory</option>
                      <option value="database">Database</option>
                    </select>
                    <InputError messages={errors['backup_config.backup_type']} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="frequency">Frequency *</Label>
                    <select
                      id="frequency"
                      name="backup_config[frequency]"
                      required
                      defaultValue={backup_config.frequency}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                    <InputError messages={errors['backup_config.frequency']} />
                  </div>
                </div>

                <MultiplePathsInput
                  label="Source Paths"
                  description="Required for directory backups. Add one or more paths to backup."
                  defaultPaths={backup_config.source_paths || (backup_config.source_path ? [backup_config.source_path] : [])}
                  name="backup_config[source_paths]"
                  placeholder="/var/www/myapp"
                />
                <InputError messages={errors['backup_config.source_paths']} />

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="destination_type">Destination *</Label>
                    <select
                      id="destination_type"
                      name="backup_config[destination_type]"
                      required
                      defaultValue={backup_config.destination_type}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="local">Local Storage</option>
                      <option value="s3">Amazon S3</option>
                    </select>
                    <InputError messages={errors['backup_config.destination_type']} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="schedule_time">Schedule Time</Label>
                    <Input
                      id="schedule_time"
                      name="backup_config[schedule_time]"
                      type="time"
                      defaultValue={backup_config.schedule_time}
                    />
                    <InputError messages={errors['backup_config.schedule_time']} />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="destination_path">Destination Path *</Label>
                  <Input
                    id="destination_path"
                    name="backup_config[destination_path]"
                    type="text"
                    required
                    defaultValue={backup_config.destination_path || ''}
                    placeholder="/backups/myapp or s3-bucket/prefix"
                  />
                  <InputError messages={errors['backup_config.destination_path']} />
                </div>

                <div className="border-t pt-4">
                  <h4 className="mb-4 font-medium">Retention Settings</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="retention_daily">Daily Backups</Label>
                      <Input
                        id="retention_daily"
                        name="backup_config[retention_daily]"
                        type="number"
                        min="1"
                        defaultValue={backup_config.retention_daily}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="retention_weekly">Weekly Backups</Label>
                      <Input
                        id="retention_weekly"
                        name="backup_config[retention_weekly]"
                        type="number"
                        min="1"
                        defaultValue={backup_config.retention_weekly}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="retention_monthly">Monthly Backups</Label>
                      <Input
                        id="retention_monthly"
                        name="backup_config[retention_monthly]"
                        type="number"
                        min="1"
                        defaultValue={backup_config.retention_monthly}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="enabled"
                    name="backup_config[enabled]"
                    value="true"
                    defaultChecked={backup_config.enabled}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="enabled" className="text-sm font-normal">
                    Enable this backup configuration
                  </Label>
                </div>

                <div className="flex justify-between">
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDelete}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Configuration
                  </Button>
                  <div className="flex gap-4">
                    <Button type="button" variant="outline" onClick={() => history.back()}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={processing}>
                      {processing && <Spinner />}
                      Save Changes
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Form>
        </CardContent>
      </Card>
      </div>
    </AppLayout>
  )
}
